import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { USER_ROLES } from '@/constants';
import { usePageConfig } from '@/contexts/PageConfigContext';
import {
  UserWidget,
  LeaderboardWidget,
  NextMatch,
  PredictionActivity,
  MyTournaments,
  PredictionStats,
  GamesCalendar
} from '@/widgets';
import { useTranslation } from 'react-i18next';
import stadiumCover from '@/assets/images/world-cup-2026.webp';
import styles from './HomePage.module.scss';

// Featured tournament ID for leaderboard widget
const FEATURED_TOURNAMENT_ID = 32;

const HomePage = () => {
  const { t } = useTranslation(['common', 'tournament']);
  const { user } = useAuth();

  usePageConfig({ title: 'PClash', showNavigation: false });

  // Fetch featured tournament data
  const { data: tournamentData } = useQuery({
    queryKey: ['tournament', FEATURED_TOURNAMENT_ID],
    queryFn: async () => {
      const response = await apiClient.getTournament(String(FEATURED_TOURNAMENT_ID));
      return response.data?.data || response.data || response;
    },
    placeholderData: keepPreviousData,
  });

  // Fetch leaderboard for featured tournament
  const { data: leaderboardData } = useQuery({
    queryKey: ['tournament-leaderboard', FEATURED_TOURNAMENT_ID],
    queryFn: async () => {
      const response = await apiClient.getTournamentLeaderboard(String(FEATURED_TOURNAMENT_ID), 8);
      return response.data?.data || response.data || response;
    },
    placeholderData: keepPreviousData,
  });

  const leaderboardEntries = leaderboardData?.leaderboard || [];

  return (
    <div className={styles.dashboard}>
      {/* Row 1: User Stats + Next Match */}
      <div className={styles.row}>
        <div className={styles.colMain}>
          <UserWidget
            name={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'User'}
            role={[user?.city, user?.country].filter(Boolean).join(', ') || '\u00A0'}
            avatarUrl={user?.avatar_url}
            coverUrl={stadiumCover}
          />
        </div>
        <div className={styles.colSide}>
          <NextMatch />
        </div>
      </div>

      {/* Row 2: My Tournaments + Leaderboard */}
      <div className={styles.row}>
        <div className={styles.colMain}>
          <MyTournaments />
        </div>
        <div className={styles.colSide}>
          <LeaderboardWidget
            title={<>{tournamentData?.leagueName || t('tournament:tournament')} <span className="d-block">{t('tournament:leaderboard')}</span></>}
            img={tournamentData?.leagueLogo}
            entries={leaderboardEntries}
            variant="minimal"
            linkTo={`/tournaments/${FEATURED_TOURNAMENT_ID}/leaderboard`}
            maxItems={6}
          />
        </div>
      </div>

      {/* Row 3: Prediction Activity (full width) */}
      <div className={styles.fullWidth}>
        <PredictionActivity />
      </div>

      {/* Row 4: Prediction Stats + Games Calendar */}
      <div className={styles.row}>
        <div className={styles.colMain}>
          <PredictionStats />
        </div>
        <div className={styles.colSide}>
          <GamesCalendar />
        </div>
      </div>

      {/* User Status */}
      <div className={styles.status}>
        <span className="ls-tag ls-tag--accent">
          {t('signedInAs')} {user?.email}
          {user?.role === USER_ROLES.ADMIN && ' (ADMIN)'}
        </span>
      </div>
    </div>
  );
};

export default HomePage;
